/**
 * Paisaje desbloqueable de Galia ("la casa de Galia")
 * ---------------------------------------------------
 * Un fondo tipo rompecabezas que se arma pieza por pieza a medida que el
 * estudiante progresa. Distinto por nivel MCER (A1/A2/B1/B2).
 *
 * Idea original: reunión con Santiago (02/06/2026). El estudiante arranca con
 * un lienzo casi vacío ("apenas empecé") y va descubriendo piezas hasta armar
 * la casa de Galia. Cada nivel MCER tiene su propio paisaje.
 *
 * NOTA DE ALCANCE (en dev):
 *  - Cada pieza se revela con SU trofeo de logros-por-acción (1 pieza ↔ 1
 *    trofeo, ver SCENE_PIECES); solo el sol es de cortesía por métrica.
 *  - El arte es SVG placeholder generado por código. Un diseñador puede
 *    reemplazar cada `<g data-piece="...">` por la ilustración final sin tocar
 *    esta lógica (lienzo 800×600, ver GaliaScene.tsx).
 */

import { MCERLevel } from '../types';
import { trophiesForLevel } from './trophies';
import { isKioskMode } from '../utils/featureFlags';

/** Métricas de progreso ya disponibles de las que se derivan las piezas. */
export type ProgressMetric = 'level' | 'streak' | 'interactions';

export interface ProgressStats {
  /** Nivel de gamificación 1–10 (gamificationService.getCurrentLevel). */
  level: number;
  /** Racha de días consecutivos 0–5. */
  streak: number;
  /** Total de interacciones (mensajes) acumuladas. */
  interactions: number;
}

/** Condición de desbloqueo de una pieza: `metric >= gte`. */
export interface PieceUnlock {
  metric: ProgressMetric;
  gte: number;
}

export interface ScenePiece {
  /** Id estable — debe coincidir con el `data-piece` del SVG. */
  id: string;
  /** Etiqueta legible (para el hint "descubre: el árbol"). Español. */
  label: string;
  /**
   * Condición por métrica de progreso (`metric >= gte`). Opcional: las piezas
   * ligadas a un trofeo ya no la usan.
   */
  unlock?: PieceUnlock;
  /**
   * Se revela cuando este logro por acción está desbloqueado (id de
   * ACTION_ACHIEVEMENTS). Ej.: saludar 3 veces → revela el árbol.
   */
  achievement?: string;
}

/**
 * Orden de construcción del paisaje: de cielo vacío a casa completa.
 * El mismo esqueleto de piezas se usa en los 4 niveles; lo que cambia por
 * nivel es la PALETA (ver SCENE_PALETTES). Así "la casa de Galia" se lee en
 * todos los niveles pero cada uno tiene su propia atmósfera.
 *
 * 1 pieza ↔ 1 trofeo (feedback Santiago 2026-07-09): al principio las piezas
 * se derivaban de métricas acumuladas (interacciones/racha/nivel), pero para
 * usuarios con historial TODO aparecía revelado desde la primera carga y
 * ganar un trofeo no cambiaba nada visible. Ahora cada trofeo revela SU pieza
 * (causalidad clara: acción en francés → trofeo → el paisaje crece). Solo el
 * sol es de cortesía, para que la escena nunca arranque totalmente vacía.
 */
export const SCENE_PIECES: ScenePiece[] = [
  { id: 'sun',         label: 'el sol',           unlock: { metric: 'level', gte: 1 } },
  { id: 'hills',       label: 'las colinas',      achievement: 'introduce' },
  { id: 'tree',        label: 'el árbol',         achievement: 'greeting' },
  { id: 'house-body',  label: 'la casa',          achievement: 'thanks' },
  { id: 'house-roof',  label: 'el techo',         achievement: 'politeness' },
  { id: 'door-window', label: 'puerta y ventana', achievement: 'question' },
  { id: 'birds',       label: 'los pájaros',      achievement: 'farewell' },
  { id: 'garden',      label: 'el jardín',        achievement: 'opinion' },
];

/**
 * Trofeo (spec Excel) que revela cada pieza para el NIVEL del estudiante:
 * la pieza n-ésima (sin contar el sol) corresponde al n-ésimo trofeo de
 * bronce del catálogo del nivel. Así el mismo paisaje sirve para A1..C2 sin
 * mapeos manuales (la hoja C tiene 10 bronces: los 3 extra no revelan pieza).
 */
export function trophyIdForPiece(pieceId: string, level?: MCERLevel | null): string | null {
  const idx = SCENE_PIECES.findIndex((p) => p.id === pieceId);
  if (idx <= 0) return null; // sol o pieza desconocida
  const bronzes = trophiesForLevel(level).filter((t) => !t.meta);
  return bronzes[idx - 1]?.id || null;
}

/* ─────────────────────────────────────────────────────────────
   Escenas ILUSTRADAS (arte final de la AF, correo de Santiago 28/09/2026)
   Por nivel: un `fondo` (donde aparece Galia), 7 piezas PNG recortadas
   —una por trofeo de bronce, el orden de obtención es indiferente— y un
   `fondo_completo` que reemplaza todo al tener los 7. Los niveles sin arte
   siguen con el paisaje SVG provisional.
   Assets en public/assets/scenes/<nivel>/ (WebP optimizados de los PNG de
   Drive). Las posiciones se calcaron a ojo sobre fondo_completo: el diseño
   final está redibujado, así que las piezas no calzan pixel a pixel.
   ───────────────────────────────────────────────────────────── */

export interface IllustratedPiece {
  /** Nombre del archivo (sin extensión) en la carpeta de la escena. */
  id: string;
  /** Etiqueta legible para la píldora "Descubre …". Español. */
  label: string;
  /** Índice del trofeo de bronce del nivel que la revela (0-based). */
  trophy: number;
  /** Caja en px del lienzo de la escena. */
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface IllustratedScene {
  /** Nombre del lugar (para la píldora al completar). */
  place: string;
  /** Carpeta bajo /assets/scenes/. */
  dir: string;
  width: number;
  height: number;
  /** En orden de apilado: la primera queda más atrás. */
  pieces: IllustratedPiece[];
}

export const ILLUSTRATED_SCENES: Partial<Record<MCERLevel, IllustratedScene>> = {
  A1: {
    place: 'París',
    dir: 'a1',
    width: 1672,
    height: 941,
    pieces: [
      { id: 'torre',  label: 'la torre Eiffel', trophy: 6, x: 1045, y: 113, w: 284, h: 434 },
      { id: 'puente', label: 'el puente',       trophy: 2, x: 418,  y: 489, w: 920, h: 197 },
      { id: 'bateau', label: 'el barco',        trophy: 3, x: 978,  y: 630, w: 351, h: 89 },
      { id: 'arbol',  label: 'el árbol',        trophy: 1, x: 318,  y: 94,  w: 585, h: 592 },
      { id: 'cafe',   label: 'el café',         trophy: 0, x: 0,    y: 0,   w: 568, h: 828 },
      { id: 'farola', label: 'la farola',       trophy: 5, x: 552,  y: 235, w: 97,  h: 612 },
      { id: 'deck',   label: 'el puesto de libros', trophy: 4, x: 1287, y: 565, w: 401, h: 373 },
    ],
  },
  B1: {
    place: 'el Mont Saint-Michel',
    dir: 'b1',
    width: 1672,
    height: 941,
    pieces: [
      { id: 'muralla',    label: 'la muralla',    trophy: 3, x: 702,  y: 386, w: 869,  h: 162 },
      { id: 'mont',       label: 'el Mont',       trophy: 1, x: 676,  y: 92,  w: 906,  h: 455 },
      { id: 'pasarela',   label: 'la pasarela',   trophy: 0, x: 0,    y: 494, w: 1003, h: 256 },
      { id: 'gaviotas',   label: 'las gaviotas',  trophy: 2, x: 234,  y: 104, w: 334,  h: 200 },
      { id: 'vegetacion', label: 'la vegetación', trophy: 6, x: 0,    y: 549, w: 1672, h: 392 },
      { id: 'ovejitas',   label: 'las ovejitas',  trophy: 5, x: 1204, y: 668, w: 485,  h: 162 },
      { id: 'poste',      label: 'el poste',      trophy: 4, x: 0,    y: 470, w: 217,  h: 363 },
    ],
  },
};

/**
 * Escena ilustrada del nivel, o null si el nivel aún no tiene arte. El kiosco
 * (3 trofeos) sigue con el paisaje provisional: con 7 piezas nunca se completaría.
 */
export function illustratedSceneForLevel(level?: MCERLevel | null): IllustratedScene | null {
  if (!level || isKioskMode()) return null;
  return ILLUSTRATED_SCENES[level] || null;
}

/** Trofeo de bronce que revela una pieza ilustrada. */
export function trophyIdForIllustratedPiece(
  piece: IllustratedPiece,
  level: MCERLevel
): string | null {
  const bronzes = trophiesForLevel(level).filter((t) => !t.meta);
  return bronzes[piece.trophy]?.id || null;
}

export interface ScenePalette {
  /** Etiqueta de la atmósfera (para depurar / futuros textos). */
  mood: string;
  skyTop: string;
  skyBottom: string;
  celestial: string;   // sol o luna
  celestialGlow: string;
  hillsFar: string;
  hillsNear: string;
  ground: string;
  tree: string;
  trunk: string;
  houseWall: string;
  houseRoof: string;
  houseDoor: string;
  houseWindow: string;
  bird: string;
  flower: string;
  /** true → dibuja estrellas + luna en vez de sol (noche). */
  night?: boolean;
}

/** Una atmósfera distinta por nivel MCER. C1/C2 reutilizan la de B2 por ahora. */
export const SCENE_PALETTES: Record<'A1' | 'A2' | 'B1' | 'B2', ScenePalette> = {
  // A1 — amanecer: el comienzo del viaje
  A1: {
    mood: 'amanecer',
    skyTop: '#ffd9a8', skyBottom: '#ffeede',
    celestial: '#ffb347', celestialGlow: '#ffe0a3',
    hillsFar: '#f4b183', hillsNear: '#e8996a',
    ground: '#cfe3a3',
    tree: '#7fbf6a', trunk: '#9c6b4a',
    houseWall: '#fff4e6', houseRoof: '#d96c4a',
    houseDoor: '#8c5a3c', houseWindow: '#bfe3ff',
    bird: '#5a4632', flower: '#ff6f91',
  },
  // A2 — día claro
  A2: {
    mood: 'día',
    skyTop: '#8ecbff', skyBottom: '#e3f4ff',
    celestial: '#ffe14d', celestialGlow: '#fff2a3',
    hillsFar: '#9fd08a', hillsNear: '#6fb85f',
    ground: '#8fce74',
    tree: '#4fa63f', trunk: '#8a5a3a',
    houseWall: '#fffdf5', houseRoof: '#e0533b',
    houseDoor: '#7a4a2c', houseWindow: '#cdeeff',
    bird: '#40372c', flower: '#ff5c8a',
  },
  // B1 — atardecer
  B1: {
    mood: 'atardecer',
    skyTop: '#ff9e7a', skyBottom: '#ffd5a6',
    celestial: '#ff6b4a', celestialGlow: '#ffbf9c',
    hillsFar: '#a76a8f', hillsNear: '#7d4e70',
    ground: '#6b8e6a',
    tree: '#4f8a55', trunk: '#6b4530',
    houseWall: '#ffe9d6', houseRoof: '#b8433a',
    houseDoor: '#5e3826', houseWindow: '#ffe1a8',
    bird: '#3a2a30', flower: '#ffd166',
  },
  // B2 — noche estrellada
  B2: {
    mood: 'noche',
    skyTop: '#1b2a5b', skyBottom: '#3a4a86',
    celestial: '#f4f1d0', celestialGlow: '#cfd6ff',
    hillsFar: '#2c3a6b', hillsNear: '#20305c',
    ground: '#2a3f4a',
    tree: '#356b57', trunk: '#4a3628',
    houseWall: '#e8ecff', houseRoof: '#7a4a8c',
    houseDoor: '#3a2a4a', houseWindow: '#ffe08a',
    bird: '#cfd6ff', flower: '#8ad0ff',
    night: true,
  },
};

/** Resuelve la paleta para cualquier nivel MCER (C1/C2 → B2). */
export function paletteForLevel(level?: MCERLevel): ScenePalette {
  switch (level) {
    case 'A1': return SCENE_PALETTES.A1;
    case 'A2': return SCENE_PALETTES.A2;
    case 'B1': return SCENE_PALETTES.B1;
    case 'B2':
    case 'C1':
    case 'C2': return SCENE_PALETTES.B2;
    default:   return SCENE_PALETTES.A1;
  }
}

function metricValue(stats: ProgressStats, metric: ProgressMetric): number {
  switch (metric) {
    case 'level':        return stats.level;
    case 'streak':       return stats.streak;
    case 'interactions': return stats.interactions;
  }
}

/** ¿Está revelada esta pieza con las stats y logros dados? */
function isPieceRevealed(
  piece: ScenePiece,
  stats: ProgressStats,
  achievements?: Set<string>,
  level?: MCERLevel | null
): boolean {
  const byMetric = !!piece.unlock && metricValue(stats, piece.unlock.metric) >= piece.unlock.gte;
  const byAchievement =
    !!piece.achievement && !!achievements && achievements.has(piece.achievement);
  const trophyId = trophyIdForPiece(piece.id, level);
  const byTrophy = !!trophyId && !!achievements && achievements.has(trophyId);
  return byMetric || byAchievement || byTrophy;
}

/** Devuelve el conjunto de ids de piezas reveladas para las stats/logros dados. */
export function revealedPieceIds(
  stats: ProgressStats,
  achievements?: Set<string>,
  level?: MCERLevel | null
): Set<string> {
  const revealed = new Set<string>();
  for (const piece of SCENE_PIECES) {
    if (isPieceRevealed(piece, stats, achievements, level)) revealed.add(piece.id);
  }
  return revealed;
}

/** Primera pieza aún bloqueada (para el hint de "próximo objetivo"). null si completo. */
export function nextLockedPiece(
  stats: ProgressStats,
  achievements?: Set<string>
): ScenePiece | null {
  for (const piece of SCENE_PIECES) {
    if (!isPieceRevealed(piece, stats, achievements)) return piece;
  }
  return null;
}
